import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Star, Users, Check, ShieldCheck, ArrowLeft, Info } from "lucide-react";
import { api } from "@/api";
import Reveal, { usePageMeta } from "@/components/Reveal";

export default function PropertyDetail() {
  const { key } = useParams();
  const [p, setP] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    setActiveImg(0);
    api.get(`/properties/${key}`)
      .then((r) => setP(r.data))
      .catch(() => setNotFound(true));
  }, [key]);

  usePageMeta(p ? p.name : "Logement", p ? `${p.name} — ${p.location}. ${p.capacity}. Réservez sur Airbnb avec Golden Aura Pro.` : "");

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-40 text-center" data-testid="property-not-found">
        <h1 className="font-display text-3xl text-[#F6F1E6] mb-6">Logement introuvable</h1>
        <Link to="/logements" className="outline-button rounded-full px-8 py-3 text-xs tracking-[0.18em] uppercase" data-testid="back-to-properties">
          Retour aux logements
        </Link>
      </div>
    );
  }

  if (!p) {
    return <div className="py-40 text-center text-[#9AA3B5]" data-testid="property-loading">Chargement…</div>;
  }

  return (
    <div data-testid="property-detail-page">
      {/* Hero image */}
      <section className="relative h-[55vh] sm:h-[65vh] overflow-hidden">
        <motion.img
          key={p.images?.[activeImg]}
          src={p.images?.[activeImg]}
          alt={p.name}
          initial={{ opacity: 0.4, scale: 1.05 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
          className="w-full h-full object-cover"
          data-testid="property-main-image"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F1E] via-[#0A0F1E]/30 to-[#0A0F1E]/20" />
        <Link
          to="/logements"
          className="absolute top-6 left-6 flex items-center gap-2 rounded-full bg-[#0A0F1E]/70 backdrop-blur px-4 py-2 text-xs tracking-[0.15em] uppercase text-[#E5C158] border border-[#D4AF37]/30 hover:bg-[#0A0F1E]/90 transition-colors"
          data-testid="back-link"
        >
          <ArrowLeft size={14} /> Nos logements
        </Link>
        <div className="absolute bottom-10 left-6 sm:left-12 right-6">
          <Reveal>
            <p className="flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-[#D4AF37] mb-3">
              <MapPin size={14} /> {p.location}
            </p>
            <h1 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl text-[#F6F1E6]" data-testid="property-title">{p.name}</h1>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-8">
          {/* Gallery */}
          {p.images?.length > 1 && (
            <Reveal className="grid grid-cols-3 gap-3 mb-14" data-testid="property-gallery">
              {p.images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImg(i)}
                  data-testid={`gallery-thumb-${i}`}
                  className={`relative rounded-2xl overflow-hidden h-24 sm:h-36 transition-all duration-500 ${
                    activeImg === i ? "ring-2 ring-[#D4AF37] opacity-100" : "opacity-60 hover:opacity-100"
                  }`}
                >
                  <img src={img} alt={`${p.name} — photo ${i + 1}`} loading="lazy" className="w-full h-full object-cover" />
                </button>
              ))}
            </Reveal>
          )}

          <Reveal className="mb-14">
            <p className="text-xs tracking-[0.3em] uppercase text-[#D4AF37] mb-5">La résidence</p>
            <p className="text-base md:text-lg text-[#C4CAD8] leading-relaxed" data-testid="property-description">{p.description}</p>
          </Reveal>

          <Reveal className="mb-14">
            <p className="text-xs tracking-[0.3em] uppercase text-[#D4AF37] mb-6">Équipements &amp; atouts</p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4" data-testid="property-amenities">
              {p.amenities?.map((a) => (
                <li key={a} className="flex items-start gap-3 text-sm text-[#B9BFD0]">
                  <Check size={16} className="text-[#D4AF37] mt-0.5 shrink-0" /> {a}
                </li>
              ))}
            </ul>
          </Reveal>

          {p.traveler_info && (
            <Reveal>
              <div className="rounded-2xl border border-[#D4AF37]/20 bg-[#101A2E]/70 p-7 flex items-start gap-4" data-testid="traveler-info">
                <Info size={20} className="text-[#D4AF37] shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs tracking-[0.25em] uppercase text-[#D4AF37] mb-2">Informations voyageurs</p>
                  <p className="text-sm text-[#B9BFD0] leading-relaxed">{p.traveler_info}</p>
                </div>
              </div>
            </Reveal>
          )}
        </div>

        {/* Booking card */}
        <aside className="lg:col-span-4">
          <Reveal delay={0.1}>
            <div className="lg:sticky lg:top-28 rounded-3xl gold-frame bg-[#101A2E] p-8" data-testid="booking-card">
              <p className="text-xs tracking-[0.3em] uppercase text-[#D4AF37] mb-5">Réserver ce séjour</p>
              {p.rating && (
                <p className="flex items-center gap-2 text-sm text-[#E5C158] mb-3" data-testid="booking-rating">
                  <Star size={15} fill="currentColor" /> {p.rating}
                </p>
              )}
              <p className="flex items-center gap-2 text-sm text-[#9AA3B5] mb-8" data-testid="booking-capacity">
                <Users size={15} className="text-[#D4AF37]" /> {p.capacity}
              </p>
              <div className="space-y-3">
                {p.airbnb_url && (
                  <a href={p.airbnb_url} target="_blank" rel="noopener noreferrer" data-testid="detail-airbnb-btn" className="gold-button block text-center rounded-full px-6 py-4 text-[11px] font-medium tracking-[0.16em] uppercase">
                    Réserver sur Airbnb
                  </a>
                )}
                {p.booking_url ? (
                  <a href={p.booking_url} target="_blank" rel="noopener noreferrer" data-testid="detail-booking-btn" className="outline-button block text-center rounded-full px-6 py-4 text-[11px] font-medium tracking-[0.16em] uppercase">
                    Réserver sur Booking.com
                  </a>
                ) : (
                  <p className="text-center text-xs text-[#6B7385] italic py-2" data-testid="booking-unavailable">
                    Annonce Booking.com en cours de configuration
                  </p>
                )}
              </div>
              <div className="mt-8 pt-6 border-t border-[#D4AF37]/15 flex items-start gap-3">
                <ShieldCheck size={18} className="text-[#D4AF37] shrink-0 mt-0.5" />
                <p className="text-xs text-[#9AA3B5] leading-relaxed">
                  Paiement et réservation sécurisés directement sur la plateforme. Aucune donnée bancaire n'est collectée sur ce site.
                </p>
              </div>
            </div>
          </Reveal>
        </aside>
      </section>
    </div>
  );
}
